/* istanbul ignore file  */
import usePZOStorage from '@composables/usePZOStorage';
import queryString from '../utils/getQueryString';
import getPathParams from '../utils/getPathParams';

const $http = require('@vue-util/http');
const { Cookie, UserStatus } = require('@common-vue/util/src/modules/cookie');

const keysToRemove = ['Sortby', 'Pageindex', 'Productsperpage'];

function isHtmlOrInvalidJson(data) {
  if (data == null) return true;
  if (typeof data === 'string' && data.trim().startsWith('<')) return true;
  if (typeof data === 'object' && !Array.isArray(data)) return false;
  return true;
}

const facetsXapiRequest = async (pathname, info) => {
  let canvasData;
  // Remove pagination info from the facets xapi call
  let partOfPathname = pathname.split('/');
  for (let i = 0; i < partOfPathname.length - 1; i++) {
    const subParts = partOfPathname[i].split(',');
    keysToRemove.forEach((key) => {
      const index = subParts.indexOf(key);
      if (index !== -1) {
        const nextSubParts = partOfPathname[i + 1].split(',');
        if (index < nextSubParts.length) {
          nextSubParts.splice(index, 1);
        }
        partOfPathname[i + 1] = nextSubParts.join(',');
        subParts.splice(index, 1);
      }
    });
    partOfPathname[i] = subParts.join(',');
  }
  partOfPathname = partOfPathname.filter((part) => part !== '');
  const result = `/${partOfPathname.join('/')}`;
  // Facets xAPI URL with params (encode pathname so | in facet values is valid)
  let XapiUrl = `/xapi/discover/v1/page?pathname=${encodeURI(result)}`;
  const utagMain = Cookie.get('utag_main') || '';
  const utagId = (utagMain.indexOf('v_id:') !== -1) ? utagMain.split('v_id:')[1].split('$')[0] : '';
  const adobeVisitorId = Cookie.get('AMCV_8D0867C25245AE650A490D4C@AdobeOrg') || '';
  const visitorId = (adobeVisitorId.indexOf('MCMID') !== -1) ? decodeURI(adobeVisitorId).split('MCMID')[1].split('|')[1] : '';
  XapiUrl += info?.productId ? `&id=${info.productId}` : '';
  const categoryIdForFacets = info.CategoryID ?? info.id ?? info.categoryId;
  if (categoryIdForFacets != null && categoryIdForFacets !== '') {
    XapiUrl += `&id=${encodeURIComponent(categoryIdForFacets)}&CategoryID=${encodeURIComponent(categoryIdForFacets)}`;
  }
  try {
    const params = {
      xapi: 1,
      // _navigationType: info?.navigationType,
      _regionCode: Cookie.get('shippingCountry'),
      currencyCode: Cookie.get('currency'),
      facetsOnly: info?.facetsOnly,
      keyword: info?.keyword ? info?.keyword : info?.featuredName,
      _shoppingMode: info?.isWeddingRegistry?.value ? 'WEDDING_REGISTRY' : 'SITE',
      spItemsVersion: '1.1',
      utagId,
      visitorId,
      _deviceType: info?.deviceType,
      pageIndex: 1,
      productsPerPage: info?.perPage,
      sortBy: info?.sortBy,

    };

    if (info?._ffSearchQuery) {
      params.inGridSubSearchKeyword = info?._ffSearchQuery;
    }

    [
      { label: 'productId', value: info.queryProductId },
      { label: '_customerExperiment', value: info._customerExperiment },
    ].forEach(({ label, value }) => {
      if (value) {
        params[label] = value;
      }
    });

    const response = await $http.get(XapiUrl, {
      maxRedirects: 1,
      params,
    });
    if (response && !isHtmlOrInvalidJson(response.data)) {
      canvasData = response.data;
    } else {
      canvasData = {};
      if (response?.data && typeof response.data === 'string') {
        console.error('Facets xAPI returned HTML instead of JSON (check URL/proxy)'); // eslint-disable-line no-console
      }
    }
  } catch (error) {
    canvasData = {};
    console.error('Error: ', error); // eslint-disable-line no-console
    throw error;
  }
  return canvasData;
};

const getPZOBadgingProductList = async (productIds) => {
  const userGuid = Cookie.get('macys_online_guid');
  const userId = Cookie.get('macys_online_uid');
  const PZO_USER_ELIGIBLE = 'X-User-Pzo-Eligible';
  const pzoUrl = '/api/order/v3/promotions/pzoProducts';
  let canvasData = null;
  const productIdList = productIds.value;
  const { doPzoStorageExistsForThisUserId, setPzoStorage } = usePZOStorage();
  const pzoStoreExists = doPzoStorageExistsForThisUserId(userId);
  if ((UserStatus.isSignedIn() || UserStatus.isSoftSignedIn()) && productIdList.length && !pzoStoreExists) {
    try {
      const response = await $http.get(pzoUrl, {
        params: {
          userId,
          productIds: productIdList.toString(),
        },
        headers: {
          'X-Macys-User-Guid': userGuid,
          'X-Macys-ClientId': 'NavApp',
        },
      });
      if (response.headers[PZO_USER_ELIGIBLE.toLowerCase()] === 'false') {
        setPzoStorage({ userId, timestamp: Date.now() });
      }
      canvasData = response.data;
    } catch (error) {
      console.error(`Unable to read PZO eligible products from list - Status Code ${error.response.status} (${error.response.statusText}) - ${error.message}`, error); // eslint-disable-line no-console
      throw error;
    }
  }
  return canvasData;
};

const saveMySizesToggleXapi = async (deviceType, payload, headers) => {
  let canvasData;
  const XapiUrl = `/xapi/preferences/v1/sizefacetstoggle?_deviceType=${deviceType}&_source=Discovery_PAGE`;
  try {
    const response = await $http.post(XapiUrl, payload, {
      headers,
    });
    if (response) {
      canvasData = response.data;
    }
  } catch (error) {
    canvasData = {};
    console.error('Error: ', error); // eslint-disable-line no-console
    throw error;
  }
  return canvasData;
};

const loadDiscoveryPage = async (parameters) => {
  const {
    type,
    conversationalSearchFilter,
    conversationId,
    navigationType,
    productId,
    keyword,
    Sortby: initialSortBy,
    Pageindex: initialPageIndex,
    Productsperpage: initialProductsPerPage,
    route,
    deviceType,
    customerExperiment,
  } = parameters;

  let reqParamUrl;
  let canvasData;
  let shoppingMode;
  let pagination;
  const { params, query } = route;

  // Log page type
  console.info(`Page type: ${type}`); // eslint-disable-line no-console

  if (type === 'registrySearch') {
    shoppingMode = 'WEDDING_REGISTRY';
  } else if (params?.pathMatch?.indexOf('wedding-registry') !== -1) {
    // Browse and Catsplash and Brand Index page
    shoppingMode = 'WEDDING_REGISTRY';
  }

  const endPathIndex = route.fullPath.indexOf('?') > -1 ? route.fullPath.indexOf('?') : route.fullPath.length;
  reqParamUrl = route.fullPath.slice('0', endPathIndex);

  // Sorting and pagination
  let Sortby = initialSortBy;
  let Pageindex = initialPageIndex;
  let Productsperpage = initialProductsPerPage;

  // Do not apply params to xapi URL if they are pagination/sorting
  const pathMatch = reqParamUrl.split('/');
  const paramsLength = pathMatch.length;

  /* /shop/mother-denim?id=1206859 => ['', 'shop', 'mother-denim'] */
  if (paramsLength > 3) {
    const possibleFacetValues = pathMatch[paramsLength - 1]?.split(/,(?! )/) || [];
    const possibleFacetKeys = pathMatch[paramsLength - 2]?.split(',') || [];

    const sortAndPaginationParams = {};

    keysToRemove.forEach((key) => {
      const keyIndex = possibleFacetKeys.findIndex((facetName) => facetName.toLowerCase() === key.toLowerCase());
      if (keyIndex > -1) {
        sortAndPaginationParams[key] = possibleFacetValues[keyIndex];
        possibleFacetKeys.splice(keyIndex, 1);
        possibleFacetValues.splice(keyIndex, 1);
      }
    });

    const pathMatchNoFilters = pathMatch.slice(0, pathMatch.length - 2);
    if (possibleFacetKeys.length) {
      reqParamUrl = [...pathMatchNoFilters, possibleFacetKeys.join(','), possibleFacetValues.join(',')].join('/');
    } else {
      reqParamUrl = [...pathMatchNoFilters].join('/');
    }

    Sortby = sortAndPaginationParams.Sortby || Sortby;
    Pageindex = parseInt(sortAndPaginationParams.Pageindex, 10) || Pageindex;
    Productsperpage = parseInt(sortAndPaginationParams.Productsperpage, 10) || Productsperpage;
    pagination = [{ key: 'Sortby', value: Sortby ?? 'ORIGINAL' }, { key: 'Pageindex', value: Pageindex ?? 1 }, { key: 'Productsperpage', value: Productsperpage ?? 60 }];
  }

  reqParamUrl = productId ? `${reqParamUrl}&id=${productId}` : `${reqParamUrl}`;

  // Construct xAPI url (encode pathname so | and other chars in facet values are valid).
  // id and CategoryID must come right after pathname, before _navigationType.
  const { productId: queryProductId, CategoryID: categoryIdFromQuery, id: queryId } = query;
  const categoryIdForRequest = parameters.CategoryID ?? parameters.id ?? parameters.categoryId ?? categoryIdFromQuery ?? queryId;
  let XapiUrl = `/xapi/discover/v1/page?pathname=${encodeURI(reqParamUrl)}`;
  if (categoryIdForRequest != null && categoryIdForRequest !== '') {
    XapiUrl += `&id=${encodeURIComponent(categoryIdForRequest)}&CategoryID=${encodeURIComponent(categoryIdForRequest)}`;
  }

  const unparsedCookie = Cookie.get('AMCV_8D0867C25245AE650A490D4C@AdobeOrg') || Cookie.get('AMCV_8D0867C25245AE650A490D4C%40AdobeOrg');
  let visitorId = '';
  let reqCookiesAmcvSplited;

  if (unparsedCookie) {
    reqCookiesAmcvSplited = unparsedCookie.split('|');
    const positionOfMcmid = reqCookiesAmcvSplited.indexOf('MCMID');
    if (positionOfMcmid !== -1) {
      visitorId = reqCookiesAmcvSplited[positionOfMcmid + 1];
    }
  }

  // Save cookie value
  const cookie = typeof document !== 'undefined' ? document.cookie : null;
  const customerState = Cookie.getCustomerStateFromCookie(cookie) || 'GUEST';
  const updatedNavigationType = navigationType?.toUpperCase() || 'SEARCH';
  const searchKeywordParam = type?.toUpperCase() === 'SEARCH' ? `&keyword=${keyword}` : '';
  const utagMain = Cookie.get('utag_main') || '';
  const utagId = (utagMain.indexOf('v_id:') !== -1) ? utagMain.split('v_id:')[1].split('$')[0] : '';
  const _regionCode = Cookie.get('shippingCountry');
  const currencyCode = Cookie.get('currency');
  const userId = Cookie.get('macys_online_uid') || Cookie.get('bloomingdales_online_uid') || null;
  const customerId = Cookie.get('macys_online_guid') || Cookie.get('bloomingdales_online_guid') || '';

  // Get value from URL
  const url = route?.fullPath;
  const search = url.substring(url.indexOf('?'));
  const requestParams = queryString(search);
  const { _application } = getPathParams(requestParams);

  // Location cookies
  const shippingCountry = Cookie.getShippingCountryFromCookie(cookie) || 'US';
  const CSL = Cookie.get('CSL');
  const SFL = Cookie.get('SFL');
  let additionalStoreLocations = requestParams._additionalStoreLocations ? `&_additionalStoreLocations=${requestParams._additionalStoreLocations}` : '';
  let shipFromLocations = '';

  try {
    if (url.indexOf('Upc_bops_purchasable') === -1 && (cookie && CSL)) {
      let STRL = Cookie.getMultiValueCookie('STRL', 'CSL', cookie);
      STRL = STRL || (parseInt(CSL, 10) ? CSL : false);
      if (STRL) {
        additionalStoreLocations = `&_additionalStoreLocations=${STRL}`;
      }
    }
    if (shippingCountry === 'US' && (cookie && SFL) && SFL !== '' && SFL !== '0000') {
      shipFromLocations = `&_shipFromLocations=${SFL}`;
    } else if (shippingCountry === 'US' && (cookie && CSL)) {
      let nSFL = Cookie.getMultiValueCookie('SFL', 'CSL', cookie);
      nSFL = (nSFL && nSFL !== '' && nSFL !== '0000') ? nSFL : false;
      if (nSFL) {
        shipFromLocations = `&_shipFromLocations=${nSFL}`;
      }
    }
  } catch (error) {
    console.error('An error occurred while processing locations:', error); // eslint-disable-line no-console
  }

  // Add params to xAPI url
  // XapiUrl += `${searchKeywordParam}&_navigationType=${updatedNavigationType}&_shoppingMode=${shoppingMode || 'SITE'}&sortBy=${Sortby || 'ORIGINAL'}&productsPerPage=${Productsperpage || '60'}&pageIndex=${Pageindex || '1'}${_application ? `&_application=${_application}` : ''}${shipFromLocations}${additionalStoreLocations}&_regionCode=${_regionCode || 'US'}&currencyCode=${currencyCode || 'USD'}&size=medium&spItemsVersion=1.1${utagId ? `&utagId=${utagId}` : ''}&visitorId=${visitorId}&customerId=${customerId}&_deviceType=${deviceType || 'DESKTOP'}${customerState ? `&_customerState=${customerState}` : ''}`;
  XapiUrl += `${searchKeywordParam}&_shoppingMode=${shoppingMode || 'SITE'}&sortBy=${Sortby || 'ORIGINAL'}&productsPerPage=${Productsperpage || '60'}&pageIndex=${Pageindex || '1'}${_application ? `&_application=${_application}` : ''}&_regionCode=${_regionCode || 'US'}&currencyCode=${currencyCode || 'USD'}&_deviceType=${deviceType || 'DESKTOP'}`;

  if (requestParams._ffSearchQuery) {
    XapiUrl += `&inGridSubSearchKeyword=${requestParams._ffSearchQuery}`;
  }
  if (userId && (UserStatus.isSignedIn() || UserStatus.isSoftSignedIn())) {
    XapiUrl = `${XapiUrl}&userid=${userId}`;
  }

  if (customerExperiment) {
    XapiUrl = `${XapiUrl}&_customerExperiment=${customerExperiment}`;
  }
  if (requestParams.debugLog === '1') {
    XapiUrl += '&debugLog=1';
  }
  [
    // convo filters params
    { label: 'conversationId', value: conversationId },
    { label: 'conversationalSearchFilter', value: conversationalSearchFilter },
    // GWP query parameters
    { label: 'productId', value: queryProductId },
  ].forEach(({ label, value }) => {
    if (value) {
      XapiUrl = `${XapiUrl}&${label}=${value}`;
    }
  });

  // Log xAPI url details
  if (process.env.NODE_ENV !== 'production') {
    console.info(`xApi URL with params: ${XapiUrl}`); // eslint-disable-line no-console
  }

  try {
    const response = await $http.get(XapiUrl, {
      maxRedirects: 1,
    });
    if (response && !isHtmlOrInvalidJson(response.data)) {
      canvasData = response.data;
    } else {
      canvasData = {};
      if (response?.data && typeof response.data === 'string') {
        console.error('Discovery xAPI returned HTML instead of JSON (check URL/proxy)'); // eslint-disable-line no-console
      }
    }
  } catch (error) {
    canvasData = {};
    console.error('Error: ', error); // eslint-disable-line no-console
  }

  return { canvasData, pagination };
};

export default {
  loadDiscoveryPage,
  facetsXapiRequest,
  getPZOBadgingProductList,
  saveMySizesToggleXapi,
};
